import { useCallback, useEffect, useState } from 'react'

import { learningApi, type LearningEpisode, type LearningTutorial } from '@/api'

export function useLearningTutorials(params?: { categoryCode?: string; limit?: number }) {
  const [items, setItems] = useState<LearningTutorial[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await learningApi.listTutorials({
        limit: params?.limit ?? 24,
        category_code: params?.categoryCode || undefined,
      })
      setItems(data.items ?? [])
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [params?.categoryCode, params?.limit])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { items, loading, error, refresh }
}

export function useLearningTutorial(id: number | null) {
  const [tutorial, setTutorial] = useState<LearningTutorial | null>(null)
  const [episodes, setEpisodes] = useState<LearningEpisode[]>([])
  const [loading, setLoading] = useState(Boolean(id))
  const [error, setError] = useState<unknown>(null)

  const refresh = useCallback(async () => {
    if (id == null || !Number.isFinite(id)) return
    setLoading(true)
    setError(null)
    try {
      const [t, eps] = await Promise.all([
        learningApi.getTutorial(id),
        learningApi.listEpisodes(id, { limit: 100, offset: 0 }),
      ])
      setTutorial(t)
      setEpisodes(eps.items ?? [])
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { tutorial, episodes, loading, error, refresh }
}
